/* ==========================================================================
   GT Policy Page — Table of Contents
   - Parses every <h2> inside .gt-policy-page__body
   - Wraps each H2 + its following content in a .gt-policy-block (for spacing)
   - Assigns deterministic anchor IDs from the H2 text
   - Builds a sticky aside list of links from those headings
   - Scroll-spies the active section using IntersectionObserver
   - Smooth-scroll on click with sticky-header offset
   - Mobile (≤1024px): horizontal-scroll TOC, auto-centers active item
   ========================================================================== */

(function () {
  'use strict';

  function init() {
    var pages = document.querySelectorAll('[data-gt-policy-page]');
    if (!pages.length) return;
    pages.forEach(setupPage);
  }

  function slugify(text, fallback) {
    var s = (text || '')
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[‘’“”]/g, '')
      .replace(/&[a-z]+;/g, ' ')
      .replace(/[^\w\s-]/g, ' ')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
    if (!s) s = fallback;
    return 'gt-policy-' + s;
  }

  function getHeaderOffset() {
    // Use sticky header height (if present) plus a comfortable gap.
    var header = document.querySelector('.header-wrapper, .shopify-section-header, .site-header, .header-group');
    var h = header ? header.offsetHeight : 80;
    return h + 24;
  }

  function setupPage(page) {
    var body = page.querySelector('[data-gt-policy-body]');
    var tocList = page.querySelector('[data-gt-policy-toc-list]');
    var aside = page.querySelector('[data-gt-policy-toc]');
    if (!body || !tocList) return;

    var introLabel = page.getAttribute('data-gt-intro-label') || 'Introduction';
    var stripUpdated = page.getAttribute('data-gt-strip-updated') !== 'false';

    // Step 1a — strip duplicate H1s from the body. The section already renders
    // page.title as the canonical H1; any H1 the merchant put in page.content
    // is a duplicate. Drop them outright.
    var bodyH1s = body.querySelectorAll('h1');
    bodyH1s.forEach(function (h1) {
      if (h1.parentNode) h1.parentNode.removeChild(h1);
    });

    // Step 1b — strip a duplicate "Last updated:" line from the intro area.
    // Match ANY element type (p, div, h3-h6, span...) whose trimmed text
    // starts with "Last updated:" / "Updated:" — Shopify's RTE wraps this
    // line inconsistently across stores.
    var firstH2 = body.querySelector('h2');
    if (stripUpdated) {
      var updatedRe = /^\s*(last\s+updated|updated|last\s+modified|modified)\s*[:\-–—]/i;
      var n = body.firstChild;
      while (n && n !== firstH2) {
        var nx = n.nextSibling;
        if (n.nodeType === 1) {
          var txt = (n.textContent || '').trim();
          if (txt && updatedRe.test(txt)) {
            n.parentNode.removeChild(n);
          }
        } else if (n.nodeType === 3) {
          // Bare text node directly inside body
          var t = n.textContent.trim();
          if (t && updatedRe.test(t)) {
            n.parentNode.removeChild(n);
          }
        }
        n = nx;
      }
    }

    // Step 2 — if there is any remaining content before the first H2 (or
    // page is all intro with no H2s), wrap that intro content in a
    // .gt-policy-block with id "gt-policy-introduction". No visible H2 is
    // injected — the sidebar TOC entry for the intro is added programmatically
    // below, but the heading itself does not appear in the content area.
    firstH2 = body.querySelector('h2');
    var introNodes = [];
    var introHasContent = false;
    var probe = body.firstChild;
    while (probe && probe !== firstH2) {
      var nxProbe = probe.nextSibling;
      if (probe.nodeType === 1 && (probe.textContent || '').trim().length > 0) introHasContent = true;
      else if (probe.nodeType === 3 && probe.textContent.trim().length > 0) introHasContent = true;
      introNodes.push(probe);
      probe = nxProbe;
    }

    var introEntry = null;
    if (introHasContent) {
      var introBlock = document.createElement('div');
      introBlock.className = 'gt-policy-block gt-policy-block--intro';
      introBlock.id = 'gt-policy-introduction';
      body.insertBefore(introBlock, body.firstChild);
      introNodes.forEach(function (node) {
        introBlock.appendChild(node);
      });
      introEntry = { id: 'gt-policy-introduction', label: introLabel, h2: introBlock };
    }

    var headings = body.querySelectorAll('h2');
    if (!headings.length && !introEntry) {
      // No H2s and no intro content — collapse to single-column layout.
      page.classList.add('gt-policy-page--no-toc');
      if (aside) aside.setAttribute('hidden', '');
      return;
    }

    // We have H2s or an intro block — reveal the aside and switch to two-column layout.
    page.classList.remove('gt-policy-page--no-toc');
    if (aside) aside.removeAttribute('hidden');

    var usedIds = {};
    var entries = [];
    if (introEntry) {
      usedIds[introEntry.id] = true;
      entries.push(introEntry);
    }

    headings.forEach(function (h2, idx) {
      var label = (h2.textContent || '').trim();
      var id = h2.id;
      if (!id) {
        id = slugify(label, 'section-' + (idx + 1));
        // Ensure uniqueness
        var unique = id;
        var n = 2;
        while (usedIds[unique] || document.getElementById(unique)) {
          unique = id + '-' + n;
          n++;
        }
        id = unique;
        h2.id = id;
      }
      usedIds[id] = true;

      // Wrap h2 + following siblings up to next h2 in a .gt-policy-block
      // so the visual rhythm matches the reference articles.
      var block = document.createElement('div');
      block.className = 'gt-policy-block';
      var parent = h2.parentNode;
      parent.insertBefore(block, h2);
      var node = h2;
      while (node && !(node !== h2 && node.nodeType === 1 && node.tagName === 'H2')) {
        var next = node.nextSibling;
        block.appendChild(node);
        node = next;
      }

      entries.push({ id: id, label: label, h2: h2 });
    });

    // Build TOC list
    var frag = document.createDocumentFragment();
    var links = [];
    entries.forEach(function (entry) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + entry.id;
      a.textContent = entry.label;
      a.setAttribute('data-gt-policy-link', entry.id);
      li.appendChild(a);
      frag.appendChild(li);
      links.push(a);
    });
    tocList.innerHTML = '';
    tocList.appendChild(frag);

    // Smooth scroll on click with sticky-header offset
    links.forEach(function (a) {
      a.addEventListener('click', function (e) {
        var id = a.getAttribute('href').slice(1);
        var target = document.getElementById(id);
        if (!target) return;
        e.preventDefault();
        var offset = getHeaderOffset();
        var top = target.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        // Update URL hash without jumping
        if (history.replaceState) history.replaceState(null, '', '#' + id);
        setActive(a, links, tocList);
      });
    });

    // Scroll-spy via IntersectionObserver
    setupScrollSpy(entries, links, tocList);

    // Honor initial hash if present
    if (location.hash) {
      var initialId = location.hash.slice(1);
      var match = links.filter(function (l) {
        return l.getAttribute('data-gt-policy-link') === initialId;
      })[0];
      if (match) setActive(match, links, tocList);
    } else if (links[0]) {
      setActive(links[0], links, tocList);
    }
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function setActive(activeLink, links, tocList) {
    if (!activeLink) return;
    links.forEach(function (l) { l.classList.remove('is-active'); });
    activeLink.classList.add('is-active');
    // On mobile horizontal scrollers, center the active chip
    if (tocList && window.matchMedia('(max-width: 1024px)').matches) {
      var lLeft = activeLink.offsetLeft;
      var lW = activeLink.offsetWidth;
      var tW = tocList.clientWidth;
      var target = lLeft - (tW / 2) + (lW / 2);
      tocList.scrollTo({ left: Math.max(0, target), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
  }

  function setupScrollSpy(entries, links, tocList) {
    if (!('IntersectionObserver' in window)) {
      // Fallback: scroll-position based
      var fallback = function () {
        var pos = window.pageYOffset + getHeaderOffset() + 8;
        var current = entries[0];
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].h2.offsetTop <= pos) current = entries[i];
        }
        if (current) {
          var l = links.filter(function (a) {
            return a.getAttribute('data-gt-policy-link') === current.id;
          })[0];
          if (l) setActive(l, links, tocList);
        }
      };
      window.addEventListener('scroll', fallback, { passive: true });
      fallback();
      return;
    }

    // Track visibility ratio per heading-block, pick the topmost visible one
    var visibility = {};
    entries.forEach(function (e) { visibility[e.id] = 0; });

    var observer = new IntersectionObserver(function (records) {
      records.forEach(function (r) {
        var id = r.target.id;
        visibility[id] = r.isIntersecting ? r.intersectionRatio || 0.001 : 0;
      });
      // Pick the entry whose H2 is closest to (but not past) the offset line
      var offset = getHeaderOffset() + 8;
      var current = null;
      var bestTop = -Infinity;
      entries.forEach(function (e) {
        var top = e.h2.getBoundingClientRect().top;
        if (top - offset <= 0 && top > bestTop) {
          bestTop = top;
          current = e;
        }
      });
      if (!current) {
        // None passed the line yet — pick the first visible
        for (var i = 0; i < entries.length; i++) {
          if (visibility[entries[i].id] > 0) {
            current = entries[i];
            break;
          }
        }
      }
      if (!current) current = entries[0];
      var link = links.filter(function (a) {
        return a.getAttribute('data-gt-policy-link') === current.id;
      })[0];
      if (link) setActive(link, links, tocList);
    }, {
      // Trigger band roughly aligned with sticky header
      rootMargin: '-100px 0px -55% 0px',
      threshold: [0, 0.25, 0.5, 1]
    });

    entries.forEach(function (e) { observer.observe(e.h2); });

    // Also re-evaluate on window resize (header height may change)
    window.addEventListener('resize', function () {
      // Force a recompute by toggling observation
      // (cheap trick: trigger a synthetic scroll event)
      window.dispatchEvent(new Event('scroll'));
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
